import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/openplay/OpenPlayIcon';
import EventHeader from './EventHeader';
import EventDetails from './EventDetails';
import ParticipantList from './ParticipantList';
import InquirySection from './InquirySection';
import ActionButtons from './ActionButtons';
import { supabase } from '../../../lib/supabaseClient';

const EventDetailsInteractive = ({ eventData, participants: initialParticipants, inquiries: initialInquiries, token }) => {
  const navigate = useNavigate();
  const [isHydrated, setIsHydrated] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [participants, setParticipants] = useState(initialParticipants);
  const [inquiries, setInquiries] = useState(initialInquiries);
  const [showSuccessToast, setShowSuccessToast] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
          <div className="h-32 rounded-xl bg-muted animate-pulse" />
          <div className="h-64 rounded-xl bg-muted animate-pulse" />
          <div className="h-48 rounded-xl bg-muted animate-pulse" />
        </div>
      </div>
    );
  }

  const isFull = eventData.currentParticipants >= eventData.maxParticipants;

  const requireLoginAndGo = async (targetPath) => {
    try {
      const { data } = await supabase.auth.getSession();
      const authed = Boolean(data?.session?.user);
      if (!authed) {
        navigate('/openplay/user-authentication', { state: { from: targetPath } });
        return;
      }
      navigate(targetPath);
    } catch {
      navigate('/openplay/user-authentication', { state: { from: targetPath } });
    }
  };

  const handleApplyClick = () => {
    if (!token) {
      alert('이벤트 토큰 정보를 찾을 수 없습니다.');
      return;
    }
    requireLoginAndGo(`/public-session-page?token=${encodeURIComponent(token)}&open=apply`);
  };

  const handleInquiryClick = () => {
    if (!token) {
      alert('이벤트 토큰 정보를 찾을 수 없습니다.');
      return;
    }
    requireLoginAndGo(`/public-session-page?token=${encodeURIComponent(token)}&open=inquiry`);
  };

  const handleToggleBookmark = () => {
    setIsBookmarked(!isBookmarked);
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="min-h-screen bg-background pb-24 md:pb-6">
      <header className="sticky top-0 z-navigation bg-card border-b border-border shadow-elevation-1">
        <div className="flex items-center h-[72px] px-4 max-w-4xl mx-auto">
          <button
            onClick={handleBack}
            className="flex items-center justify-center w-10 h-10 rounded-lg text-foreground hover:bg-muted transition-smooth touch-target mr-2"
            aria-label="뒤로가기"
            type="button"
          >
            <Icon name="ChevronLeftIcon" size={24} />
          </button>
          <h1 className="text-lg font-semibold font-heading text-foreground">이벤트 상세</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-8">
        <section className="bg-card rounded-xl p-6 shadow-elevation-1">
          <EventHeader
            sportType={eventData.sportType}
            sportIcon={eventData.sportIcon}
            organizationName={eventData.organizationName}
            organizationLogo={eventData.organizationLogo}
            organizationLogoAlt={eventData.organizationLogoAlt}
            coverImageUrl={eventData.coverImageUrl}
            imageUrls={eventData.imageUrls}
            location={eventData.location}
            currentParticipants={eventData.currentParticipants}
            maxParticipants={eventData.maxParticipants}
            rating={eventData.rating}
            totalEvents={eventData.totalEvents}
          />
        </section>

        <section className="bg-card rounded-xl p-6 shadow-elevation-1">
          <EventDetails
            date={eventData.date}
            time={eventData.time}
            duration={eventData.duration}
            skillLevel={eventData.skillLevel}
            description={eventData.description}
            facilityInfo={eventData.facilityInfo}
            equipment={eventData.equipment}
          />
        </section>

        <section className="bg-card rounded-xl p-6 shadow-elevation-1">
          <ParticipantList participants={participants} maxParticipants={eventData.maxParticipants} />
        </section>

        <section className="bg-card rounded-xl p-6 shadow-elevation-1">
          <InquirySection inquiries={inquiries} onAskQuestion={handleInquiryClick} />
        </section>
      </main>

      <ActionButtons
        isFull={isFull}
        isBookmarked={isBookmarked}
        chatRoomUrl={eventData.chatRoomUrl}
        onApply={handleApplyClick}
        onToggleBookmark={handleToggleBookmark}
      />

      {showSuccessToast && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-toast">
          <div className="flex items-center gap-3 px-6 py-4 rounded-lg bg-success text-success-foreground shadow-elevation-4 animate-slide-down">
            <Icon name="CheckCircleIcon" variant="solid" size={24} />
            <span className="font-medium">성공적으로 처리되었습니다</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDetailsInteractive;
